import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { BarChart3, Ban, Folder, Link as LinkIcon, ScrollText, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  ErrorPage,
  FeedbackState,
  PageContent,
  PageHeader,
  PageShell,
  PageSkeleton,
  StatusBadge,
  Tabs,
} from "@h2j-data-team/ui";
import { BackLink } from "@/components/back-link";
import {
  appAnalysisDetailQueryOptions,
  appAnalysisKeys,
  isQlikEvaluationPending,
  useStartAppAnalysis,
} from "@/features/app-analysis/api";
import { connectionQueryOptions } from "@/features/qlik-connections/api";
import { jobQueryOptions, useCancelJob } from "@/features/jobs/api";
import { FieldsAnalysisTable } from "@/components/app-analysis/FieldsAnalysisTable";
import { VariablesAnalysisTable } from "@/components/app-analysis/VariablesAnalysisTable";
import { DimensionsAnalysisTable } from "@/components/app-analysis/DimensionsAnalysisTable";
import { MeasuresAnalysisTable } from "@/components/app-analysis/MeasuresAnalysisTable";
import { ObjectsAnalysisTable } from "@/components/app-analysis/ObjectsAnalysisTable";
import { QlikEvaluationPanel } from "@/components/app-analysis/QlikEvaluationPanel";
import { AnalysisStatusBadge } from "@/components/app-analysis/AnalysisStatusBadge";
import { AnalysisResultSkeleton } from "@/components/app-analysis/AnalysisResultSkeleton";
import { AppAnalysisLogsDrawer } from "@/components/app-analysis/AppAnalysisLogs";
import { CompareAnalysisMenu } from "@/components/app-analysis/CompareAnalysisMenu";
import { HealthGradeBadge } from "@/components/app-analysis/HealthGradeBadge";
import { HealthOverviewTab } from "@/components/app-analysis/HealthOverviewTab";
import {
  DimensionsInlineSummary,
  FieldsInlineSummary,
  MeasuresInlineSummary,
  VariablesInlineSummary,
} from "@/components/app-analysis/InlineSummaries";
import { actionErrorDetail } from "@/components/app-analysis/load-error";
import { supportCode } from "@/components/error-screen";
import { ApiError } from "@/lib/api-client";
import { useFormatDate } from "@/hooks/use-format-date";
import type { AppAnalysisDetail } from "@repo/shared";

export const Route = createFileRoute("/_authenticated/analyses/$analysisId")({
  staticData: { pageTitle: "breadcrumbs.analyses", breadcrumb: "breadcrumbs.analysisDetails" },
  component: AnalysisDetailPage,
});

// The tab panels share the page's height with the table inside them: the active
// panel is a column that fills the tabs and scrolls on its own, so a table card
// takes what is left and scrolls its rows while the header stays put.
const TAB_PANELS =
  "min-h-0 flex-1 [&>[role=tabpanel]:not([hidden])]:flex [&>[role=tabpanel]:not([hidden])]:min-h-0 [&>[role=tabpanel]:not([hidden])]:flex-1 [&>[role=tabpanel]:not([hidden])]:flex-col [&>[role=tabpanel]:not([hidden])]:gap-4 [&>[role=tabpanel]:not([hidden])]:overflow-auto";

function BackToAnalyses() {
  const { t } = useTranslation("analyses");
  return <BackLink to="/analyses" label={t("detail.backToAnalyses")} />;
}

function AnalysisDetailPage() {
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const { formatDateTime } = useFormatDate();
  const { analysisId } = useParams({
    from: "/_authenticated/analyses/$analysisId",
  });
  const navigate = useNavigate();
  const startAnalysis = useStartAppAnalysis();
  const [logsOpen, setLogsOpen] = useState(false);
  const logsOpenerRef = useRef<HTMLElement | null>(null);
  const [tab, setTab] = useState<string | null>(null);

  const {
    data: analysis,
    isLoading,
    error,
    refetch,
  } = useQuery(appAnalysisDetailQueryOptions(analysisId));
  const { data: connection } = useQuery(connectionQueryOptions(analysis?.connectionId ?? ""));

  const handleNewAnalysis = async () => {
    if (!analysis) return;

    try {
      const created = await startAnalysis.mutateAsync({
        connectionId: analysis.connectionId,
        appId: analysis.appId,
        appName: analysis.appName,
        spaceName: analysis.spaceName ?? undefined,
        // The optional phases are deliberately NOT sent: this button says "New
        // Analysis", and a new analysis runs the product default (Qlik on,
        // measurement off) from startAppAnalysisSchema. Inferring the previous
        // run's intent from its OUTPUT is what used to break it — every
        // analysis predating the Qlik phase has `qlikEvaluation: null`, so the
        // re-run silently opted out of the default. To choose the phases, use
        // the new-analysis drawer, which has the two checkboxes.
      });
      navigate({ to: "/analyses/$analysisId", params: { analysisId: created.id } });
    } catch {
      // Error surfaced via startAnalysis.error below
    }
  };

  if (isLoading) {
    return <PageSkeleton variant="detail" label={t("detail.loadingAnalysis")} />;
  }

  if (error || !analysis) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <ErrorPage
        kind="load-failed"
        header={<PageHeader title={t("detail.errorTitle")} back={<BackToAnalyses />} />}
        title={notFound ? t("detail.analysisNotFound") : tc("error.loadFailed")}
        supportCode={supportCode(error, tc("error.supportCode"))}
        actions={
          notFound ? undefined : (
            <Button variant="outline" onClick={() => void refetch()}>
              {tc("tryAgain")}
            </Button>
          )
        }
      />
    );
  }

  const status = analysis.status ?? "completed";

  // A cancelled/failed batch may preserve its static results and completed contexts.
  if (status === "processing") {
    return <AnalysisProcessingView analysis={analysis} connectionName={connection?.name ?? null} />;
  }
  const interrupted = status === "failed" || status === "cancelled";
  if (interrupted && !analysis.summary) {
    return (
      <AnalysisTerminalView
        analysis={analysis}
        connectionName={connection?.name ?? null}
        onNewAnalysis={handleNewAnalysis}
        isStarting={startAnalysis.isPending}
        startError={startAnalysis.error}
      />
    );
  }

  // Completed but (defensively) missing a result payload — show a minimal state
  // rather than crashing on summary/fields.
  const summary = analysis.summary;
  if (!summary) {
    return (
      <PageShell
        header={
          <PageHeader
            eyebrow={t("detail.eyebrow")}
            title={analysis.appName}
            back={<BackToAnalyses />}
          />
        }
      >
        <PageContent>
          <FeedbackState state="empty" title={t("detail.resultUnavailable")} />
        </PageContent>
      </PageShell>
    );
  }

  const health = analysis.health ?? null;
  const activeTab = tab ?? (health ? "overview" : "fields");

  const analyzedAtLabel = formatDateTime(analysis.analyzedAt);

  const tabLabel = (name: string, count: number) => (
    <>
      {name}
      <span className="ml-1.5 font-mono text-[11px] text-faint tabular-nums">{count}</span>
    </>
  );

  return (
    <PageShell
      header={
        <PageHeader
          title={analysis.appName}
          back={<BackToAnalyses />}
          meta={
            <span className="font-mono">
              {[connection?.name, analysis.spaceName, t("detail.staticDiagnosis"), analyzedAtLabel]
                .filter(Boolean)
                .join(" · ")}
            </span>
          }
          actions={
            <>
              {interrupted && <AnalysisStatusBadge status={status} />}
              {health && (
                <HealthGradeBadge grade={health.grade} score={health.score} variant="status" />
              )}
              {isQlikEvaluationPending(analysis.qlikEvaluation?.status) && (
                <StatusBadge tone="info">
                  {t(`qlikEvaluation.state.${analysis.qlikEvaluation?.status}.title`)}
                </StatusBadge>
              )}
              <CompareAnalysisMenu analysis={analysis} />
              <Button
                variant="outline"
                size="sm"
                onClick={(event) => {
                  logsOpenerRef.current = event.currentTarget;
                  setLogsOpen(true);
                }}
              >
                <ScrollText />
                {t("logs.button")}
              </Button>
              <Button size="sm" loading={startAnalysis.isPending} onClick={handleNewAnalysis}>
                {!startAnalysis.isPending && <BarChart3 />}
                {startAnalysis.isPending ? t("detail.startingAnalysis") : t("detail.newAnalysis")}
              </Button>
            </>
          }
        />
      }
    >
      <PageContent fill>
        {interrupted && (
          <Alert tone="warning" className="mb-4">
            <p>{t("lifecycle.preservedResults")}</p>
            {analysis.error && <p className="mt-1">{analysis.error}</p>}
          </Alert>
        )}
        {startAnalysis.error && (
          <Alert tone="danger" title={t("dialog.jobFailed")} className="mb-4">
            {actionErrorDetail(startAnalysis.error, tc("error.supportCode"))}
          </Alert>
        )}

        <Tabs
          className={TAB_PANELS}
          label={t("detail.tabsLabel")}
          value={activeTab}
          onValueChange={setTab}
          items={[
            ...(health
              ? [
                  {
                    value: "overview",
                    label: t("detail.tabs.overview"),
                    content: <HealthOverviewTab summary={summary} health={health} />,
                  },
                ]
              : []),
            {
              value: "objects",
              label: tabLabel(t("detail.tabs.objects"), analysis.objects.length),
              content: (
                <>
                  <QlikEvaluationPanel evaluation={analysis.qlikEvaluation} />
                  <ObjectsAnalysisTable
                    analysisId={analysisId}
                    objects={analysis.objects}
                    qlikEvaluation={analysis.qlikEvaluation}
                    objectTimings={analysis.objectTimings}
                  />
                </>
              ),
            },
            {
              value: "fields",
              label: tabLabel(t("detail.tabs.fields"), analysis.fields.length),
              content: (
                <>
                  <FieldsInlineSummary summary={summary} />
                  <FieldsAnalysisTable
                    analysisId={analysisId}
                    fields={analysis.fields}
                    objects={analysis.objects}
                  />
                </>
              ),
            },
            {
              value: "variables",
              label: tabLabel(t("detail.tabs.variables"), analysis.variables.length),
              content: (
                <>
                  <VariablesInlineSummary summary={summary} />
                  <VariablesAnalysisTable
                    analysisId={analysisId}
                    variables={analysis.variables}
                    objects={analysis.objects}
                  />
                </>
              ),
            },
            {
              value: "dimensions",
              label: tabLabel(t("detail.tabs.dimensions"), analysis.dimensions.length),
              content: (
                <>
                  <DimensionsInlineSummary summary={summary} />
                  <DimensionsAnalysisTable
                    analysisId={analysisId}
                    dimensions={analysis.dimensions}
                    objects={analysis.objects}
                  />
                </>
              ),
            },
            {
              value: "measures",
              label: tabLabel(t("detail.tabs.measures"), analysis.measures.length),
              content: (
                <>
                  <MeasuresInlineSummary summary={summary} />
                  <MeasuresAnalysisTable
                    analysisId={analysisId}
                    measures={analysis.measures}
                    objects={analysis.objects}
                  />
                </>
              ),
            },
          ]}
        />
      </PageContent>

      <AppAnalysisLogsDrawer
        open={logsOpen}
        onOpenChange={setLogsOpen}
        restoreFocusRef={logsOpenerRef}
        analysisId={analysisId}
        active={false}
      />
    </PageShell>
  );
}

// Connection and space under the title, shared by the lifecycle views.
function LifecycleMeta({
  connectionName,
  spaceName,
}: {
  connectionName: string | null;
  spaceName: string | null;
}) {
  const { t } = useTranslation("analyses");
  if (!connectionName && !spaceName) return null;
  return (
    <>
      {connectionName && (
        <span className="flex items-center gap-1.5" title={t("table.connection")}>
          <LinkIcon className="size-3.5 text-faint" strokeWidth={1.8} aria-hidden />
          {connectionName}
        </span>
      )}
      {spaceName && (
        <span className="flex items-center gap-1.5" title={t("table.space")}>
          <Folder className="size-3.5 text-faint" strokeWidth={1.8} aria-hidden />
          {spaceName}
        </span>
      )}
    </>
  );
}

// Processing view (Option B): a skeleton of the completed layout so the page
// keeps its shape while the analysis runs. Live job progress + cancel sit in the
// header; the live worker log stream is behind the Logs button (as on the
// completed screen).
function AnalysisProcessingView({
  analysis,
  connectionName,
}: {
  analysis: AppAnalysisDetail;
  connectionName: string | null;
}) {
  const { t } = useTranslation("analyses");
  const queryClient = useQueryClient();
  const cancelJob = useCancelJob();
  const [logsOpen, setLogsOpen] = useState(false);
  const logsOpenerRef = useRef<HTMLElement | null>(null);

  // Poll the linked job for live progress.
  const { data: job } = useQuery({
    ...jobQueryOptions(analysis.jobId ?? ""),
    enabled: !!analysis.jobId,
  });
  const progress = job?.progress ?? null;

  const handleCancel = () => {
    if (!analysis.jobId) return;
    cancelJob.mutate(analysis.jobId, {
      // Reflect the cancel immediately rather than waiting for the next poll.
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: appAnalysisKeys.detail(analysis.id) }),
    });
  };

  return (
    <PageShell
      header={
        <PageHeader
          title={analysis.appName}
          back={<BackToAnalyses />}
          meta={
            <span className="font-mono">
              {[connectionName, analysis.spaceName].filter(Boolean).join(" · ")}
            </span>
          }
          actions={
            <>
              <AnalysisStatusBadge status="processing" />
              <Button
                variant="outline"
                size="sm"
                onClick={(event) => {
                  logsOpenerRef.current = event.currentTarget;
                  setLogsOpen(true);
                }}
              >
                <ScrollText />
                {t("logs.button")}
              </Button>
              {analysis.jobId && (
                <Button
                  variant="outline"
                  size="sm"
                  loading={cancelJob.isPending}
                  onClick={handleCancel}
                >
                  {t("lifecycle.cancel")}
                </Button>
              )}
            </>
          }
        />
      }
    >
      <PageContent>
        <AnalysisResultSkeleton progress={progress} />
      </PageContent>

      <AppAnalysisLogsDrawer
        open={logsOpen}
        onOpenChange={setLogsOpen}
        restoreFocusRef={logsOpenerRef}
        analysisId={analysis.id}
        active
      />
    </PageShell>
  );
}

// Terminal view (Option B) for a failed/cancelled analysis. Keeps the same page
// shell as the completed/processing screens (header + a content area), with a
// clean empty state in place of the result and the diagnostic logs behind the
// Logs button — rather than a distinct error-banner + inline-logs layout.
function AnalysisTerminalView({
  analysis,
  connectionName,
  onNewAnalysis,
  isStarting,
  startError,
}: {
  analysis: AppAnalysisDetail;
  connectionName: string | null;
  onNewAnalysis: () => void;
  isStarting: boolean;
  startError: Error | null;
}) {
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const [logsOpen, setLogsOpen] = useState(false);
  const logsOpenerRef = useRef<HTMLElement | null>(null);
  const status = analysis.status ?? "failed";
  const cancelled = status === "cancelled";

  return (
    <PageShell
      header={
        <PageHeader
          eyebrow={t("detail.eyebrow")}
          title={analysis.appName}
          back={<BackToAnalyses />}
          meta={
            <LifecycleMeta connectionName={connectionName} spaceName={analysis.spaceName ?? null} />
          }
          actions={
            <>
              <AnalysisStatusBadge status={status} />
              <Button
                variant="outline"
                size="sm"
                onClick={(event) => {
                  logsOpenerRef.current = event.currentTarget;
                  setLogsOpen(true);
                }}
              >
                <ScrollText />
                {t("logs.button")}
              </Button>
              <Button size="sm" loading={isStarting} onClick={onNewAnalysis}>
                {!isStarting && <BarChart3 />}
                {t("detail.newAnalysis")}
              </Button>
            </>
          }
        />
      }
    >
      <PageContent className="flex flex-col gap-6">
        {startError && (
          <Alert tone="danger" title={t("dialog.jobFailed")}>
            {actionErrorDetail(startError, tc("error.supportCode"))}
          </Alert>
        )}

        <FeedbackState
          state={cancelled ? "empty" : "error"}
          icon={cancelled ? <Ban /> : <TriangleAlert />}
          title={cancelled ? t("lifecycle.cancelledTitle") : t("lifecycle.failedTitle")}
          description={
            analysis.error ??
            (cancelled ? t("lifecycle.cancelledDescription") : t("lifecycle.failedDescription"))
          }
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={(event) => {
                logsOpenerRef.current = event.currentTarget;
                setLogsOpen(true);
              }}
            >
              <ScrollText />
              {t("lifecycle.viewLogs")}
            </Button>
          }
        />
      </PageContent>

      <AppAnalysisLogsDrawer
        open={logsOpen}
        onOpenChange={setLogsOpen}
        restoreFocusRef={logsOpenerRef}
        analysisId={analysis.id}
        active={false}
      />
    </PageShell>
  );
}
